/**
 * Etichette leggibili per le tabelle a soglie del regolamento
 * ("minimo → valore": fattore capitano, D Factor).
 *
 * Andrea 2026-09-11: usate sia dalla pagina Regole che dall'editor, cosi'
 * le fasce si leggono uguali dappertutto — niente piu' "Voto ≥ 4.5" da
 * una parte e "4.5" dall'altra.
 */

/** Numero senza zeri inutili: 6.5 -> "6.5", 6.25 -> "6.25", 5.0 -> "5". */
export function num(n: number): string {
  return String(Number(n.toFixed(2)));
}

/** Valore col segno davanti: +1.5 / -0.5 / 0. */
export function conSegno(valore: string | number): string {
  const n = Number(valore);
  if (!Number.isFinite(n)) return String(valore);
  return n > 0 ? `+${num(n)}` : num(n);
}

/** Etichette per una tabella a soglie a partire dai minimi in ordine
 * crescente.
 *
 * I voti vanno di mezzo punto, quindi con gradini da 0.5 si legge il
 * valore secco ("5", "5.5") come su Fantagazzetta; le medie invece sono
 * continue, e allora serve l'intervallo esatto ("≥ 6 < 6.25"), altrimenti
 * "6" farebbe pensare al solo 6 tondo. La prima riga con minimo 0 e' il
 * fondo scala: "≤ 4" / "< 6". */
export function etichetteFasce(minimi: number[]): string[] {
  if (minimi.length === 0) return [];
  const passi = minimi.slice(1).map((m, i) => m - minimi[i]);
  const passo = passi.length > 0 ? Math.min(...passi) : 0;
  const mezzoVoto =
    passo === 0.5 && minimi.every((m) => Number.isInteger(m * 2));
  return minimi.map((m, i) => {
    const succ = minimi[i + 1];
    if (succ === undefined) return `≥ ${num(m)}`;
    if (i === 0 && m === 0)
      return mezzoVoto ? `≤ ${num(succ - passo)}` : `< ${num(succ)}`;
    return mezzoVoto ? num(m) : `≥ ${num(m)} < ${num(succ)}`;
  });
}

/** Etichetta di una riga in una lista NON ordinata (es. le righe
 * dell'editor, dove l'utente puo' inserirle in qualsiasi ordine): la si
 * calcola sull'insieme ordinato e la si ritrova per valore. */
export function etichettaFascia(minimi: number[], minimo: number): string {
  const ordinati = [...new Set(minimi)].sort((a, b) => a - b);
  const et = etichetteFasce(ordinati);
  const idx = ordinati.indexOf(minimo);
  return idx >= 0 ? et[idx] : `≥ ${num(minimo)}`;
}
